import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { navigate, useParams } from "@reach/router";
import { KeyPersonnelListPage } from "../KeyPersonnel";

import { TabHeader } from "components/TabHeader";
import { SharedProfileList } from "newui/features/Company/components/SharedProfileList";
import { CompanyDocuments } from "newui/features/Company/CompanyDocuments";

import { ProjectListing } from "../../../../routes/account/companyProfile/ProjectListing";
import CompanyLocationForm from "./CompanyLocationForm";
import CompanyInformation from "./CompanyInformationForm";
import SkeletonBase from "newui/components/Skeleton";

import {
	isBidder,
	isProcurer,
	isProcurerNBidder,
} from "utils/helpers";

import {
	getCompanyView,
	getPastProjects,
	getResources,
} from "actions/companyActions";

import { useCurrentCompanyId, useCurrentUser } from "hooks/auth";
import { useModuleCan } from "newui/features/RolesPermissions/hooks/useModuleCan";
import { AppDispatch, RootState } from "newui/store";
import TabComponent from "newui/components/TabComponent";
import { Company } from "../types";

/* -------------------------------------------------------------------------- */
/* Types                                                                      */
/* -------------------------------------------------------------------------- */

interface CompanyProfileProps {
	activeTab: string;
	company: Company;
	path: string;
}

interface RouteParams {
	companyId?: string;
}

interface CompanyProfileTabMenuItem {
	id: string;
	name: string;
	disabled: boolean;
	showBadge: boolean;
	testId: string;
	total: number;
}

/* -------------------------------------------------------------------------- */
/* Component                                                                  */
/* -------------------------------------------------------------------------- */

const CompanyProfile: React.FC<CompanyProfileProps> = ({
	company,
	activeTab,
}) => {
	const dispatch = useDispatch<AppDispatch>();

	const { currentUser, isCompanyAdmin, isSuperAdmin } = useCurrentUser();
	const { canView: canViewCompanyProfile, canEdit: canEditCompanyProfile } =
		useModuleCan("company_profile");
	const {
		resources,
		requestingResources,
		pastProjects,
	} = useSelector((state: RootState) => state.companyProfile);

	const isLoading = useSelector(
		(state: RootState) => state.search.isLoading
	);

	const currentCompanyId = useCurrentCompanyId();
	const params = useParams<RouteParams>();

	const companyId =
		currentUser?.is_admin ? params?.companyId : currentCompanyId;

	/* ---------------------------------------------------------------------- */
	/* Permissions                                                             */
	/* ---------------------------------------------------------------------- */

	const allowedOfficeLocations =
		isProcurerNBidder(company) ||
		isBidder(company) ||
		isProcurer(company) ||
		isSuperAdmin;

	/*
	 * PRO-709: was `isCompanyAdmin || isSuperAdmin || isProcurer(company)`. All
	 * three are pre-RBAC signals, so a Bidder Support — who holds
	 * `company_profile = view` and whose API reads return the documents fine —
	 * failed every term and got an empty tab. `src/hooks/auth.tsx` already warns
	 * that `company_admin` "does not track RBAC standing"; this is that warning
	 * coming true. The legacy terms are retained as an OR so nobody who could
	 * reach this tab before loses it: `isProcurer` covers procurer companies
	 * whose viewers hold no bidder-side module, and the executive/superuser
	 * terms are identity grants the matrix deliberately does not model.
	 */
	const allowedDocument =
		canViewCompanyProfile || isCompanyAdmin || isSuperAdmin || isProcurer(company);

	/* ---------------------------------------------------------------------- */
	/* Constants                                                               */
	/* ---------------------------------------------------------------------- */

	const COMPANYINFORMATION = "Company Information";
	const OFFICELOCATIONS = "Office Locations";
	const PROJECTEXPERIENCES = "Projects and Experience";
	const KEYPERSONNELEXPERTISE = "Key Personnel and Expertise";
	const CORPORATEDOCUMENTS = "Corporate Documents";
	const SHAREDPROFILES = "Shared Profiles";

	/* ---------------------------------------------------------------------- */
	/* Refs                                                                    */
	/* ---------------------------------------------------------------------- */

	const formikRef = useRef<unknown>(null);

	/* ---------------------------------------------------------------------- */
	/* Tabs                                                                    */
	/* ---------------------------------------------------------------------- */

	const tabMenu: CompanyProfileTabMenuItem[] = [
		{
			id: "company-information",
			testId: "company-information",
			name: COMPANYINFORMATION,
			showBadge: false,
			total: 0,
			disabled: false,
		},
		{
			id: "company-locations",
			testId: "office-locations",
			name: OFFICELOCATIONS,
			showBadge: false,
			total: 0,
			disabled: !allowedOfficeLocations,
		},
		{
			id: "projects",
			testId: "projects-&-experiences",
			name: PROJECTEXPERIENCES,
			showBadge: false,
			total: 0,
			disabled: isProcurer(company),
		},
		{
			id: "resources",
			testId: "key-personnel-&-expertise",
			name: KEYPERSONNELEXPERTISE,
			showBadge: false,
			total: 0,
			disabled: isProcurer(company),
		},
		{
			id: "documents",
			testId: "corporate-documents",
			name: CORPORATEDOCUMENTS,
			showBadge: false,
			total: 0,
			disabled: isProcurer(company),
		},
		{
			id: "shared-companies",
			testId: "shared-companies",
			name: SHAREDPROFILES,
			showBadge: false,
			total: 0,
			disabled: false,
		},
	];

	const currentTab = tabMenu.find(
		(menu) => menu.id === activeTab
	)?.name;

	const changeActiveTab = (_name: string, id: string): void => {
		navigate(`./${id}`);
	};

	/* ---------------------------------------------------------------------- */
	/* Effects                                                                 */
	/* ---------------------------------------------------------------------- */

	useEffect(() => {
		if (!companyId) return;

		dispatch(getCompanyView(companyId));
		dispatch(getPastProjects(companyId, true));
		dispatch(getResources(companyId));
	}, [companyId, dispatch]);

	/* ---------------------------------------------------------------------- */
	/* Render                                                                  */
	/* ---------------------------------------------------------------------- */

	return (
		<div>
			<TabHeader heading="COMPANY PROFILE" description="" />

			<div className="mb-5 mt-5">
				<div className="flex gap-3">
					<TabComponent
						menus={tabMenu}
						activeTab={currentTab}
						changeActiveTab={changeActiveTab}
						isLoading={false}
					/>
				</div>
			</div>

			<div className="border-b border-gray-200" />

			{!isLoading ? (
				<div className="mt-12">
					{currentTab === COMPANYINFORMATION && (
						<CompanyInformation formikRef={formikRef} />
					)}

					{currentTab === OFFICELOCATIONS && allowedOfficeLocations && (
						<CompanyLocationForm />
					)}

					{currentTab === PROJECTEXPERIENCES && (
						/*
						 * PRO-709: ProjectListing already gates Add/Edit/Remove on
						 * its `edit` prop, but that prop defaults to true and no
						 * call site passed it — so every viewer got the mutating
						 * controls. Drive it from RBAC v2 standing instead of the
						 * default.
						 */
						<ProjectListing
							path="projects"
							projects={pastProjects}
							companyId={companyId}
							edit={canEditCompanyProfile}
						/>
					)}

					{currentTab === KEYPERSONNELEXPERTISE && (
						<KeyPersonnelListPage canEdit={canEditCompanyProfile} />
					)}

					{currentTab === CORPORATEDOCUMENTS && allowedDocument && (
						<CompanyDocuments
							profileTypeId={company?.profile_type?.id}
						/>
					)}

					{currentTab === SHAREDPROFILES && (
						<SharedProfileList />
					)}
				</div>
			) : (
				<div className="px-4">
					{Array.from({ length: 2 }).map((_, i) => (
						<div
							key={i}
							className="my-4 flex w-full items-center justify-between gap-8"
						>
							<div className="w-2/4">
								<div className="my-2">
									<SkeletonBase height="18px" />
								</div>
								<SkeletonBase width="100%" height="56px" />
							</div>
							<div className="w-2/4">
								<div className="my-2">
									<SkeletonBase height="18px" />
								</div>
								<SkeletonBase width="100%" height="56px" />
							</div>
						</div>
					))}

					<div className="w-full">
						<div className="my-2">
							<SkeletonBase height="18px" />
						</div>
						<SkeletonBase width="100%" height="200px" />
					</div>
				</div>
			)}
		</div>
	);
};

export default CompanyProfile;
